import React, { useContext } from 'react';
import { Link } from '@tanstack/react-router';
import { Stack } from '@mui/material';

import { AppContext } from 'context/app';
import { LeavesContext } from 'context/leaves';

import { NavActionButton } from 'layout/navigation/navActions/NavActionButton';

import { useAspenLogger } from 'hooks/useAspenLogger';

import { filterLeavesByDeviceType, getDeviceLeafById } from 'modules/leaves';

import styles from './livestreamNav.module.scss';

interface LivestreamNavProps {
  isGridView: boolean;
  isFocusedView: boolean;
}

export const LivestreamNav = ({ isGridView, isFocusedView }: LivestreamNavProps): React.ReactNode => {
  const { deviceOrder } = useContext(AppContext);
  const { leafStore } = useContext(LeavesContext);
  const leaves = leafStore.read();
  const { logAspenEvent } = useAspenLogger();

  const cameraLeaves = filterLeavesByDeviceType(leaves || {}, ['camera', 'doorbell']);
  const orderedCameraLeaves = deviceOrder
    .map((id) => getDeviceLeafById(cameraLeaves, id))
    .filter((leaf) => leaf !== null);
  const firstCamera = orderedCameraLeaves[0]?.id;

  return (
    <Stack direction="row" spacing={2} gap="5px">
      <Link
        aria-label="navigation-grid-view"
        data-testid="nav-action-link-grid-view"
        className={styles.link}
        to="/"
        activeOptions={{
          exact: true,
        }}
        onClick={() => {
          logAspenEvent({
            log_id: 'iot.web.stream.action',
            payload: {
              device_id: '',
              action: 'grid_view',
            },
          });
        }}
      >
        <NavActionButton tabIndex={-1} className={isGridView ? 'active' : ''}>
          Grid view
        </NavActionButton>
      </Link>
      <Link
        aria-label="navigation-focused-view"
        data-testid="nav-action-link-focused-view"
        className={styles.link}
        to="/$camera"
        params={{ camera: firstCamera }}
        onClick={() => {
          logAspenEvent({
            log_id: 'iot.web.stream.action',
            payload: {
              device_id: firstCamera,
              action: 'focused_view',
            },
          });
        }}
      >
        <NavActionButton tabIndex={-1} className={isFocusedView ? 'active' : ''}>
          Focused view
        </NavActionButton>
      </Link>
    </Stack>
  );
};
